import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useLang } from "../lib/i18n";

// Derive breadcrumb trail from current pathname using a slug → label + parent map.
// Hub-aware: every subpage points to its hub.
const PAGE_INFO = {
  "/": { label: "Start", en: "Home" },
  "/preventum-diez/": { label: "Preventum Diez", en: "Preventum Diez", parent: "/" },
  "/preventum-diez/fuer-wen/": { label: "Für wen?", en: "Who for?", parent: "/preventum-diez/" },
  "/preventum-diez-fakten/": { label: "Über Preventum Diez", en: "About Preventum Diez", parent: "/praxis-und-kontakt/" },

  "/medizin-und-untersuchungen/": { label: "Medizin & Untersuchungen", en: "Medicine & Examinations", parent: "/" },
  "/kardiologie-diez/": { label: "Kardiologie", en: "Cardiology", parent: "/medizin-und-untersuchungen/" },
  "/untersuchungen/": { label: "Untersuchungen", en: "Examinations", parent: "/medizin-und-untersuchungen/" },
  "/praevention/": { label: "Prävention", en: "Prevention", parent: "/medizin-und-untersuchungen/" },
  "/sekundaerpraevention/": { label: "Sekundärprävention", en: "Secondary prevention", parent: "/medizin-und-untersuchungen/" },
  "/sportmedizin-leistungssport/": { label: "Sportmedizin", en: "Sports medicine", parent: "/medizin-und-untersuchungen/" },

  "/wissen/": { label: "Wissen", en: "Knowledge", parent: "/" },
  "/faq/": { label: "FAQ", en: "FAQ", parent: "/wissen/" },
  "/glossar/": { label: "Glossar", en: "Glossary", parent: "/wissen/" },
  "/news/": { label: "News", en: "News", parent: "/wissen/" },
  "/wissen/sport-in-jedem-alter/": { label: "Sport in jedem Alter", en: "Sport at any age", parent: "/wissen/" },
  "/wissen/sport-nach-herzinfarkt/": { label: "Sport nach Herzinfarkt", en: "Sport after a heart attack", parent: "/wissen/" },
  "/wissen/untersuchung-vor-sportbeginn/": { label: "Untersuchung vor Sportbeginn", en: "Check before starting sport", parent: "/wissen/" },

  "/praxis-und-kontakt/": { label: "Praxis & Kontakt", en: "Practice & Contact", parent: "/" },
  "/dr-kirchner/": { label: "Dr. Kirchner", en: "Dr. Kirchner", parent: "/praxis-und-kontakt/" },
  "/ablauf/": { label: "Ablauf", en: "How it works", parent: "/praxis-und-kontakt/" },
  "/standort-diez/": { label: "Standort Diez", en: "Location Diez", parent: "/praxis-und-kontakt/" },
  "/kontakt/": { label: "Kontakt", en: "Contact", parent: "/praxis-und-kontakt/" },

  "/impressum/": { label: "Impressum", en: "Imprint", parent: "/" },
  "/datenschutz/": { label: "Datenschutz", en: "Privacy", parent: "/" },
};

function normalize(path) {
  if (!path) return "/";
  let p = path.replace(/^\/en(?=\/|$)/, "") || "/";
  if (p === "/") return "/";
  return p.endsWith("/") ? p : p + "/";
}

function getTrail(path, lang) {
  const trail = [];
  let cur = normalize(path);
  let safety = 8;
  while (cur && safety-- > 0) {
    const info = PAGE_INFO[cur];
    if (!info) break;
    trail.unshift({ to: cur, label: lang === "en" ? (info.en || info.label) : info.label });
    if (cur === "/") break;
    cur = info.parent || "/";
  }
  return trail;
}

export default function Breadcrumbs({ pathname }) {
  const { lang, t: tr, lp } = useLang();
  const trail = getTrail(pathname, lang);
  if (trail.length < 2) return null; // don't show on /

  const site = (typeof window !== "undefined" && window.location?.origin) || "https://preventum-diez.de";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.label,
      item: site + lp(t.to),
    })),
  };

  return (
    <nav aria-label={tr("breadcrumb")} data-testid="breadcrumbs" className="bg-surface border-b border-outline/40">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <div className="container-x py-3">
        <ol className="flex items-center flex-wrap gap-1 text-[13px] text-ink-muted">
          {trail.map((t, i) => {
            const last = i === trail.length - 1;
            return (
              <li key={t.to} className="flex items-center gap-1">
                {i === 0 ? (
                  last ? (
                    <span className="inline-flex items-center gap-1.5 text-navy-dark font-medium">
                      <Home className="h-3.5 w-3.5" /> {t.label}
                    </span>
                  ) : (
                    <Link to={lp(t.to)} className="inline-flex items-center gap-1.5 hover:text-navy">
                      <Home className="h-3.5 w-3.5" /> {t.label}
                    </Link>
                  )
                ) : last ? (
                  <span className="text-navy-dark font-medium" aria-current="page">{t.label}</span>
                ) : (
                  <Link to={lp(t.to)} className="hover:text-navy">{t.label}</Link>
                )}
                {!last && <ChevronRight className="h-3.5 w-3.5 text-ink-muted/60" />}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
